/* =====================================================================
 * App-scoped permission provider.
 *
 * Bridges `@auw/permissions`' pure provider with B2C's data sources:
 *   - the tenant manifest (via `useTenant()`) supplies the role tree.
 *   - `userStore` supplies the currently signed-in user's role id.
 *
 * Keeping this wrapper inside askyourai instead of shared lets
 * different apps (enterprise build, widget embed, …) plug their own
 * user stores in without inheriting B2C's specific Zustand setup.
 *
 * Role id resolution
 *   Backend responses may return a role value that the tenant doesn't
 *   recognise — e.g. a legacy `'user'` row when the tenant manifest
 *   defines only `'staff' / 'manager'`. To avoid silently granting
 *   nothing in that case we fall back to `tenant.roles.defaultRoleId`
 *   when the raw id isn't in the manifest. The fallback stays
 *   restrictive because on-prem manifests are expected to pin a
 *   viewer-style default role.
 * ===================================================================== */

import React, { useMemo, type ReactNode } from 'react';

import { PermissionProvider } from '@auw/permissions';
import { useTenant } from '@auw/tenant';

import { useUserStore } from '@auw/stores';

export interface AppPermissionProviderProps {
  readonly children: ReactNode;
}

export const AppPermissionProvider: React.FC<AppPermissionProviderProps> = ({
  children,
}) => {
  const tenant = useTenant();
  const userRoleRaw = useUserStore((s) => s.user?.role ?? null);

  const { roles, resolvedRoleId } = useMemo(() => {
    const definitions = tenant?.roles?.definitions ?? [];
    const fallback = tenant?.roles?.defaultRoleId ?? null;
    const recognised =
      userRoleRaw && definitions.some((r) => r.id === userRoleRaw)
        ? userRoleRaw
        : fallback;
    return { roles: definitions, resolvedRoleId: recognised };
  }, [tenant, userRoleRaw]);

  return (
    <PermissionProvider roles={roles} userRoleId={resolvedRoleId}>
      {children}
    </PermissionProvider>
  );
};
